import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '../../lib/locale';
import {
  X,
  Clock,
  Phone,
  User,
  CheckCircle2,
  Package,
  Utensils,
  Bike,
  Home,
  Star,
  MessageCircle,
  MapPin,
  Navigation2,
  ChevronDown,
  ChevronUp,
  Receipt,
} from 'lucide-react';
import { api } from '../../lib/api';
import { ToastContainer, useToast } from '../ui/Toast';

interface OrderTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  onRateOrder?: (order: { id: string; restaurantId: string; restaurantName?: string; rating?: number }) => void;
}

interface TrackingStage {
  id: string;
  label: string;
  description: string;
  icon: any;
  timestamp?: string;
  completed: boolean;
  current: boolean;
}

const STATUS_STAGE_INDEX: Record<string, number> = {
  CONFIRMED: 0,
  PREPARING: 1,
  READY: 2,
  OUT_FOR_DELIVERY: 3,
  DELIVERED: 5,
  COMPLETED: 5,
  CANCELLED: 0,
  PENDING: 0,
};

const getCurrentStageIndex = (status: string) => STATUS_STAGE_INDEX[String(status || '').toUpperCase()] ?? 0;

const formatStatusLabel = (status: string) =>
  String(status || '')
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const REVIEW_STORAGE_KEY = 'submitted_order_reviews';

const getStoredReview = (orderId: string) => {
  const reviews = JSON.parse(localStorage.getItem(REVIEW_STORAGE_KEY) || '{}');
  return reviews[orderId] || null;
};

const buildMarkerIcon = (label: string, bgColor: string) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
      <circle cx="22" cy="22" r="20" fill="${bgColor}" stroke="white" stroke-width="3"/>
      <text x="22" y="26" text-anchor="middle" font-size="14" font-family="Arial" font-weight="700" fill="white">${label}</text>
    </svg>
  `;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export function OrderTrackingModal({ isOpen, onClose, orderId, onRateOrder }: OrderTrackingModalProps) {
  const { formatMoney } = useLocale();
  const [order, setOrder] = useState<any>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [partner, setPartner] = useState<any>(null);
  const [partnerLocation, setPartnerLocation] = useState<{ latitude: number; longitude: number; heading?: number; speed?: number } | null>(null);
  const [customerLocation, setCustomerLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [etaMinutes, setEtaMinutes] = useState<number | null>(null);
  const [trackingMode, setTrackingMode] = useState<'api' | 'simulated' | 'none'>('none');
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [nearNotified, setNearNotified] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatText, setChatText] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ id: string; from: 'customer' | 'partner'; message: string; at: string }>>([]);
  const { toasts, removeToast, info } = useToast();

  const mapRef = useRef<HTMLDivElement | null>(null);
  const leafletRef = useRef<any>(null);
  const mapInstanceRef = useRef<any>(null);
  const partnerMarkerRef = useRef<any>(null);
  const customerMarkerRef = useRef<any>(null);
  const routeLineRef = useRef<any>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastPartnerLocationRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const simulationIntervalRef = useRef<number | null>(null);
  const simulationProgressRef = useRef<number>(0);

  useEffect(() => {
    if (!isOpen || !orderId) {
      return;
    }

    let mounted = true;

    const loadOrderDetails = async () => {
      try {
        setLoading(true);
        const response = await api.orders.getById(orderId);
        if (mounted) {
          setOrder(response?.data || null);
        }
      } catch {
        if (mounted) {
          setOrder(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadOrderDetails();
    const interval = setInterval(() => void loadOrderDetails(), 15000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [isOpen, orderId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCustomerLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => {
        // ignore
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 }
    );
  }, [isOpen]);

  const currentStatusIndex = useMemo(
    () => getCurrentStageIndex(order?.status),
    [order?.status]
  );
  const storedReview = useMemo(() => getStoredReview(orderId), [orderId, order?.updatedAt]);
  const submittedReview = order?.review || storedReview;

  const orderStatuses: TrackingStage[] = useMemo(() => {
    const stages = [
      {
        id: 'confirmed',
        label: 'Order Confirmed',
        description: 'Restaurant has confirmed your order',
        icon: CheckCircle2,
        timestamp: order?.acceptedAt,
      },
      {
        id: 'preparing',
        label: 'Preparing Food',
        description: 'Your food is being prepared',
        icon: Utensils,
        timestamp: order?.preparingAt,
      },
      {
        id: 'pickedUp',
        label: 'Picked up by Delivery Partner',
        description: 'Delivery partner has picked up your order',
        icon: Package,
        timestamp: order?.readyAt,
      },
      {
        id: 'onTheWay',
        label: 'On the Way',
        description: 'Your order is on the way to you',
        icon: Bike,
        timestamp: order?.status === 'OUT_FOR_DELIVERY' || order?.status === 'DELIVERED' ? order?.updatedAt : undefined,
      },
      {
        id: 'nearby',
        label: 'Near Your Location',
        description: 'Delivery partner is nearby',
        icon: Navigation2,
        timestamp: distanceKm !== null && distanceKm <= 0.5 ? new Date().toISOString() : undefined,
      },
      {
        id: 'delivered',
        label: 'Delivered',
        description: 'Order delivered successfully',
        icon: Home,
        timestamp: order?.deliveredAt || order?.completedAt,
      },
    ];

    return stages.map((stage, index) => {
      const isNearStage = stage.id === 'nearby';
      const isNear = distanceKm !== null && distanceKm <= 0.5;
      const completed = isNearStage
        ? isNear || currentStatusIndex >= 5
        : currentStatusIndex >= index;
      const current =
        isNearStage
          ? isNear && currentStatusIndex === 3
          : currentStatusIndex === index && currentStatusIndex < 5 && !(isNear && stage.id === 'onTheWay');
      return { ...stage, completed, current };
    });
  }, [currentStatusIndex, order, distanceKm]);

  const getEstimatedTime = () => {
    const status = String(order?.status || '').toUpperCase();

    if (status === 'DELIVERED' || status === 'COMPLETED') {
      return 'Delivered';
    }
    if (status === 'OUT_FOR_DELIVERY') {
      if (etaMinutes !== null) {
        return `${Math.max(1, Math.ceil(etaMinutes))} mins`;
      }
      return 'Arriving soon';
    }
    if (status === 'READY') {
      return 'Dispatching soon';
    }
    if (status === 'PREPARING') {
      return `${Math.max(10, Number(order?.items?.[0]?.menuItem?.preparationTime || 15))} mins`;
    }

    return 'Updating...';
  };

  const restaurantLat = order?.restaurant?.latitude !== null && order?.restaurant?.latitude !== undefined
    ? Number(order.restaurant.latitude)
    : null;
  const restaurantLng = order?.restaurant?.longitude !== null && order?.restaurant?.longitude !== undefined
    ? Number(order.restaurant.longitude)
    : null;

  useEffect(() => {
    if (!isOpen || !mapRef.current) return;
    if (mapInstanceRef.current) return;

    let disposed = false;

    const loadLeaflet = async () => {
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      if (!(window as any).L) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Leaflet'));
          document.body.appendChild(script);
        });
      }

      if (disposed || !mapRef.current) return;
      const L = (window as any).L;
      leafletRef.current = L;

      const center = customerLocation
        ? [customerLocation.latitude, customerLocation.longitude]
        : restaurantLat !== null && restaurantLng !== null
          ? [restaurantLat, restaurantLng]
          : [12.9716, 77.5946];

      const map = L.map(mapRef.current, { attributionControl: false }).setView(center, 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(map);

      mapInstanceRef.current = map;
      setTimeout(() => map.invalidateSize(), 50);
    };

    void loadLeaflet();

    return () => {
      disposed = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isOpen, customerLocation, restaurantLat, restaurantLng]);

  const animateMarker = (marker: any, from: { latitude: number; longitude: number }, to: { latitude: number; longitude: number }) => {
    if (!marker || !leafletRef.current) return;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    const start = performance.now();
    const duration = 900;
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const lat = from.latitude + (to.latitude - from.latitude) * t;
      const lng = from.longitude + (to.longitude - from.longitude) * t;
      marker.setLatLng([lat, lng]);
      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      }
    };
    animationFrameRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    const L = leafletRef.current;
    if (!L || !mapInstanceRef.current) return;

    if (customerLocation) {
      if (!customerMarkerRef.current) {
        const customerIcon = L.icon({
          iconUrl: buildMarkerIcon('YOU', '#16a34a'),
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        });
        customerMarkerRef.current = L.marker([customerLocation.latitude, customerLocation.longitude], {
          title: 'Your Location',
          icon: customerIcon,
        }).addTo(mapInstanceRef.current);
      } else {
        customerMarkerRef.current.setLatLng([customerLocation.latitude, customerLocation.longitude]);
      }
    }

    if (partnerLocation) {
      if (!partnerMarkerRef.current) {
        const icon = L.icon({
          iconUrl: buildMarkerIcon('DP', '#2563eb'),
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        });
        partnerMarkerRef.current = L.marker([partnerLocation.latitude, partnerLocation.longitude], {
          title: 'Delivery Partner',
          icon,
        }).addTo(mapInstanceRef.current);
      } else {
        const last = lastPartnerLocationRef.current;
        if (last) {
          animateMarker(partnerMarkerRef.current, last, partnerLocation);
        } else {
          partnerMarkerRef.current.setLatLng([partnerLocation.latitude, partnerLocation.longitude]);
        }
      }
      lastPartnerLocationRef.current = { latitude: partnerLocation.latitude, longitude: partnerLocation.longitude };
    }

    if (partnerLocation && customerLocation) {
      const latlngs = [
        [partnerLocation.latitude, partnerLocation.longitude],
        [customerLocation.latitude, customerLocation.longitude],
      ];
      if (!routeLineRef.current) {
        routeLineRef.current = L.polyline(latlngs, { color: '#2563eb', weight: 3, opacity: 0.8 }).addTo(mapInstanceRef.current);
      } else {
        routeLineRef.current.setLatLngs(latlngs);
      }
    }
  }, [partnerLocation, customerLocation]);

  useEffect(() => {
    if (!partnerLocation || !customerLocation) {
      setDistanceKm(null);
      setEtaMinutes(null);
      return;
    }

    const toRad = (value: number) => (value * Math.PI) / 180;
    const lat1 = partnerLocation.latitude;
    const lon1 = partnerLocation.longitude;
    const lat2 = customerLocation.latitude;
    const lon2 = customerLocation.longitude;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = 6371 * c;

    setDistanceKm(distance);

    const speed = partnerLocation.speed && partnerLocation.speed > 0 ? partnerLocation.speed : 20;
    const eta = (distance / speed) * 60;
    setEtaMinutes(eta);
  }, [partnerLocation, customerLocation]);

  useEffect(() => {
    if (distanceKm === null) return;
    if (distanceKm <= 0.5 && !nearNotified) {
      info('Your delivery partner is near your location.');
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Delivery partner is near', {
          body: 'Your delivery partner is within 500 meters of your location.',
        });
      }
      setNearNotified(true);
    }
  }, [distanceKm, nearNotified, info]);

  useEffect(() => {
    if (!isOpen || !orderId) return;
    const status = String(order?.status || '').toUpperCase();
    if (status !== 'OUT_FOR_DELIVERY' && status !== 'DELIVERED' && status !== 'COMPLETED') {
      return;
    }

    let active = true;

    const startSimulation = () => {
      if (trackingMode === 'api') return;
      if (restaurantLat === null || restaurantLng === null || !customerLocation) return;

      setTrackingMode('simulated');
      setPartner((prev: any) => prev || {
        name: 'Delivery Partner',
        phone: '+91 90000 00000',
        vehicleType: 'Bike',
        photoUrl: 'https://api.dicebear.com/7.x/avataaars/svg?seed=delivery',
      });

      if (!partnerLocation) {
        setPartnerLocation({ latitude: restaurantLat, longitude: restaurantLng, speed: 25 });
      }

      if (simulationIntervalRef.current) {
        clearInterval(simulationIntervalRef.current);
      }

      simulationIntervalRef.current = window.setInterval(() => {
        simulationProgressRef.current = Math.min(simulationProgressRef.current + 0.08, 1);
        const progress = simulationProgressRef.current;
        const nextLat = restaurantLat + (customerLocation.latitude - restaurantLat) * progress;
        const nextLng = restaurantLng + (customerLocation.longitude - restaurantLng) * progress;
        setPartnerLocation({ latitude: nextLat, longitude: nextLng, speed: 25 });
      }, 7000);
    };

    const fetchTracking = async () => {
      try {
        const response = await api.deliveryTracking.getByOrder(orderId);
        if (!active) return;
        const data = response?.data;
        if (data?.location?.latitude !== undefined && data?.location?.longitude !== undefined) {
          setTrackingMode('api');
          setTrackingError(null);
          if (simulationIntervalRef.current) {
            clearInterval(simulationIntervalRef.current);
            simulationIntervalRef.current = null;
          }
          setPartner(data.partner || null);
          setPartnerLocation({
            latitude: Number(data.location.latitude),
            longitude: Number(data.location.longitude),
            heading: data.location.heading ?? undefined,
            speed: data.location.speed ?? undefined,
          });
          return;
        }
      } catch (error: any) {
        setTrackingError(error?.message || 'Unable to fetch tracking data');
      }

      startSimulation();
    };

    void fetchTracking();
    const interval = window.setInterval(() => void fetchTracking(), 7000);

    return () => {
      active = false;
      clearInterval(interval);
      if (simulationIntervalRef.current) {
        clearInterval(simulationIntervalRef.current);
        simulationIntervalRef.current = null;
      }
    };
  }, [isOpen, orderId, order?.status, customerLocation, restaurantLat, restaurantLng, trackingMode]);

  const sendChatMessage = () => {
    const message = chatText.trim();
    if (!message) return;
    const newEntry = {
      id: `msg-${Date.now()}`,
      from: 'customer' as const,
      message,
      at: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, newEntry]);
    setChatText('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-blue-600 to-blue-700 rounded-t-2xl p-6 text-white">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">Track Your Order</h2>
              <p className="text-blue-100 text-sm">Order #{order?.orderNumber || orderId}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-blue-100">Estimated Delivery</p>
                  <p className="text-xl font-bold">{getEstimatedTime()}</p>
                </div>
              </div>
              {!loading && order && (
                <div className="text-right">
                  <p className="text-sm text-blue-100">Status</p>
                  <p className="font-semibold">{formatStatusLabel(order.status)}</p>
                  <p className="text-xs text-blue-100 mt-1">
                    {String(order.deliveryMode || '').toUpperCase() === 'RESTAURANT_SELF'
                      ? 'Delivery by Restaurant'
                      : 'Delivery by Delivery Partner'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {loading || !order ? (
            <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-600">
              Loading order tracking...
            </div>
          ) : (
            <>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-4 flex items-center justify-between border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Live Map Tracking</h3>
                  </div>
                  <div className="text-xs text-gray-500">
                    Updates every 7s {trackingMode === 'simulated' ? '(simulated)' : ''}
                  </div>
                </div>
                <div className="relative">
                  <div ref={mapRef} className="w-full" style={{ height: 260 }} />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur rounded-lg px-3 py-2 shadow">
                    <p className="text-xs text-gray-500">Distance</p>
                    <p className="font-semibold text-gray-900">
                      {distanceKm !== null ? `${distanceKm.toFixed(2)} km` : 'Calculating'}
                    </p>
                  </div>
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur rounded-lg px-3 py-2 shadow">
                    <p className="text-xs text-gray-500">ETA</p>
                    <p className="font-semibold text-gray-900">
                      {etaMinutes !== null ? `${Math.max(1, Math.ceil(etaMinutes))} mins` : 'Calculating'}
                    </p>
                  </div>
                  <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur rounded-lg px-3 py-2 shadow text-xs text-gray-700 flex items-center gap-3">
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-600 text-white font-bold text-[9px]">YOU</span>
                      You
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white font-bold text-[9px]">DP</span>
                      Partner
                    </span>
                  </div>
                </div>
                {trackingError && (
                  <div className="px-4 py-3 text-sm text-amber-700 bg-amber-50 border-t border-amber-100">
                    {trackingError}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Delivery Partner
                </h3>
                {partner ? (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full overflow-hidden border border-gray-200">
                        <img
                          src={partner.photoUrl || 'https://api.dicebear.com/7.x/avataaars/svg?seed=delivery'}
                          alt={partner.name || 'Delivery Partner'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{partner.name || 'Delivery Partner'}</p>
                        <p className="text-sm text-gray-600">{partner.vehicleType || 'Bike'}</p>
                        <p className="text-sm text-gray-600">{partner.phone || 'Contact unavailable'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <a
                        href={partner.phone ? `tel:${partner.phone}` : undefined}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                      >
                        <Phone className="w-4 h-4" />
                        Call
                      </a>
                      <button
                        onClick={() => setShowChat((value) => !value)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-100"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Chat
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">Delivery partner details will appear once the order is picked up.</p>
                )}

                {showChat && (
                  <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
                    <div className="max-h-40 overflow-y-auto space-y-2 mb-3">
                      {chatMessages.length === 0 && (
                        <p className="text-sm text-gray-500">Start a conversation with your delivery partner.</p>
                      )}
                      {chatMessages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`text-sm px-3 py-2 rounded-lg ${
                            msg.from === 'customer' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {msg.message}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        value={chatText}
                        onChange={(e) => setChatText(e.target.value)}
                        placeholder="Type a message"
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={sendChatMessage}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  Order Status
                </h3>

                <div className="space-y-4">
                      {orderStatuses.map((status, index) => {
                        const Icon = status.icon;

                        return (
                          <div key={status.id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                              status.completed
                                ? 'bg-green-500 shadow-lg shadow-green-500/30'
                                : status.current
                                ? 'bg-blue-500 shadow-lg shadow-blue-500/30 animate-pulse'
                                : 'bg-gray-300'
                            }`}
                          >
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          {index < orderStatuses.length - 1 && (
                            <div
                              className={`w-0.5 h-16 transition-all duration-500 ${
                                status.completed ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                            />
                          )}
                        </div>

                        <div className="flex-1 pb-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p
                                className={`font-semibold ${
                                  status.completed
                                    ? 'text-gray-900'
                                    : status.current
                                    ? 'text-blue-600'
                                    : 'text-gray-500'
                                }`}
                              >
                                {status.label}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">{status.description}</p>
                            </div>
                          {status.timestamp && status.completed && (
                            <p className="text-xs text-gray-500">
                              {new Date(status.timestamp).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          )}
                        </div>

                          {status.current && (
                            <div className="mt-2 flex items-center gap-2">
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                              </div>
                              <span className="text-xs text-blue-600 font-medium">In Progress</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowOrderDetails((value) => !value)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-gray-600" />
                    <span className="font-semibold text-gray-900">Order Details</span>
                  </div>
                  {showOrderDetails ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {showOrderDetails && (
                  <div className="border-t border-gray-200 p-4 space-y-4">
                    <div className="space-y-3">
                      {order.items?.map((item: any) => (
                        <div key={item.id} className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {item.quantity}x {item.menuItem?.name || item.name}
                            </p>
                            {item.notes && (
                              <p className="text-sm text-gray-500 mt-1">{item.notes}</p>
                            )}
                          </div>
                          <p className="font-medium text-gray-900">{formatMoney(item.subtotal || item.price)}</p>
                        </div>
                      ))}
                    </div>

                    <div className="pt-4 border-t border-gray-200 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Delivery Address</span>
                        <span className="text-gray-900 text-right max-w-[60%]">{order.deliveryAddress || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Payment Method</span>
                        <span className="text-gray-900">{order.paymentMethod || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Payment Status</span>
                        <span className="text-gray-900">{formatStatusLabel(order.paymentStatus || 'PENDING')}</span>
                      </div>
                      <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-100">
                        <span>Total</span>
                        <span>{formatMoney(order.total)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {String(order.status || '').toUpperCase() === 'DELIVERED' && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 text-center">
                  <Home className="w-12 h-12 text-green-600 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Order Delivered!</h3>
                  <p className="text-gray-600 mb-4">Hope you enjoyed your meal.</p>
                  <div className="flex justify-center gap-3 flex-wrap">
                    {!submittedReview ? (
                      <button
                        onClick={() =>
                          onRateOrder?.({
                            id: String(order.id || orderId),
                            restaurantId: String(order.restaurantId || order.restaurant?.id || ''),
                            restaurantName: String(order.restaurant?.name || ''),
                            rating: Number(order?.review?.rating || order.rating || 0) || undefined,
                          })
                        }
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Star className="w-4 h-4" />
                        Rate Order
                      </button>
                    ) : (
                      <div className="px-4 py-2.5 bg-white border border-green-200 rounded-lg text-green-700 text-sm font-medium">
                        You rated this order {submittedReview.rating}/5
                      </div>
                    )}
                    <button className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Get Help
                    </button>
                  </div>
                </div>
              )}

              {order.customer?.phone && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">Need help with this order?</p>
                      <p className="text-sm text-gray-600">Contact support or restaurant if needed</p>
                    </div>
                    <a
                      href={`tel:${order.customer.phone}`}
                      className="px-4 py-2 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 text-sm font-medium text-blue-700"
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </a>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <ToastContainer toasts={toasts} onRemove={removeToast} />
      </div>
    </div>
  );
}
